﻿import React, { useState } from 'react';
import { Patient, ImagingTabProps } from '../../../types/patient';
import { formatDate } from '../utils/patientUtils';
// Cornerstone/DICOM viewer temporarily disabled — too slow to build (see TE-27).
// import DicomViewer from '../../viewers/DicomViewer';

const ImagingTab: React.FC<ImagingTabProps> = ({ patient, imaging = [] }) => {
  const [activeImage, setActiveImage] = useState<any>(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const openImageViewer = (image: any) => {
    setActiveImage(image);
    setShowImageViewer(true);
  };

  const closeImageViewer = () => {
    setShowImageViewer(false);
  };

  // This would normally come from the API
  const mockImaging = [
    {
      id: 1,
      title: 'Lumbar MRI',
      date: '2024-11-05',
      category: 'MRI',
      body_part: 'Spine - Lumbar',
      provider: 'City Imaging Center',
      report_available: true,
      images_available: true,
      findings: 'L4-L5 disc herniation with moderate foraminal stenosis. Mild facet arthropathy.',
      impression: 'Moderate degenerative changes with disc herniation affecting L4 nerve root.'
    },
    {
      id: 2,
      title: 'Chest X-Ray',
      date: '2024-10-20',
      category: 'X-Ray',
      body_part: 'Chest',
      provider: 'Memorial Hospital',
      report_available: true,
      images_available: true,
      findings: 'Clear lung fields. No acute cardiopulmonary process.',
      impression: 'Normal chest X-ray.'
    },
    {
      id: 3,
      title: 'Bone Density Scan',
      date: '2024-09-15',
      category: 'DEXA',
      body_part: 'Hip and Spine',
      provider: 'Women\'s Health Center',
      report_available: true,
      images_available: false,
      findings: 'T-score -1.2 at lumbar spine, T-score -0.8 at femoral neck.',
      impression: 'Osteopenia of the lumbar spine. Normal bone density at hip.'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Quick Action Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex space-x-4">
          <button className="btn btn-primary">
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Order New Imaging
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" />
            </svg>
            Import External Results
          </button>
        </div>
        
        <div className="relative">
          <input
            type="text"
            placeholder="Search imaging..."
            className="input input-bordered"
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div role="tablist" className="tabs tabs-bordered">
        <button
          role="tab"
          onClick={() => setFilterType('all')}
          className={`tab ${filterType === 'all' ? 'tab-active' : ''}`}
        >
          All Imaging
        </button>
        <button
          role="tab"
          onClick={() => setFilterType('xray')}
          className={`tab ${filterType === 'xray' ? 'tab-active' : ''}`}
        >
          X-Rays
        </button>
        <button
          role="tab"
          onClick={() => setFilterType('mri')}
          className={`tab ${filterType === 'mri' ? 'tab-active' : ''}`}
        >
          MRIs
        </button>
        <button
          role="tab"
          onClick={() => setFilterType('ct')}
          className={`tab ${filterType === 'ct' ? 'tab-active' : ''}`}
        >
          CT Scans
        </button>
        <button
          role="tab"
          onClick={() => setFilterType('ultrasound')}
          className={`tab ${filterType === 'ultrasound' ? 'tab-active' : ''}`}
        >
          Ultrasounds
        </button>
        <button
          role="tab"
          onClick={() => setFilterType('other')}
          className={`tab ${filterType === 'other' ? 'tab-active' : ''}`}
        >
          Other Tests
        </button>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - List of Imaging Studies */}
        <div className="col-span-5 bg-base-100 rounded-lg shadow p-6 border border-accent">
          <h3 className="text-lg font-semibold text-base-content mb-4">Imaging Studies</h3>
          <div className="space-y-4">
            {mockImaging.map((image) => (
              <div 
                key={image.id} 
                className="p-4 border rounded-lg border-accent hover:bg-base-200 cursor-pointer"
                onClick={() => openImageViewer(image)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-base-content">{image.title}</h4>
                    <div className="mt-1 flex items-center text-xs text-base-content opacity-60">
                      <span>{formatDate(image.date)}</span>
                      <span className="mx-2">•</span>
                      <span>{image.provider}</span>
                    </div>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        image.category === 'MRI' ? 'bg-purple-100 text-purple-800' :
                        image.category === 'X-Ray' ? 'bg-blue-100 text-blue-800' :
                        image.category === 'CT' ? 'bg-green-100 text-green-800' :
                        image.category === 'Ultrasound' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-base-200 text-gray-800'
                      }`}>
                        {image.category}
                      </span>
                      <span className="inline-flex items-center ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-base-200 text-gray-800">
                        {image.body_part}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button className="text-gray-400 hover:text-base-content opacity-60">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button className="text-gray-400 hover:text-base-content opacity-60">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0l-4 4m4-4v13" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Selected Imaging Details */}
        <div className="col-span-7 bg-base-100 rounded-lg shadow border border-accent">
          {activeImage ? (
            <div className="h-full flex flex-col">
              {/* Header with Title and Actions */}
              <div className="p-6 border-b border-accent">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-base-content">{activeImage.title}</h3>
                    <p className="text-sm text-base-content opacity-60">{formatDate(activeImage.date)} • {activeImage.provider}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200">
                      Share
                    </button>
                    <button className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
                      Print Report
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Image Preview Area */}
              <div className="flex-1 p-6">
                {activeImage.images_available ? (
                  <div className="h-64 bg-black flex justify-center items-center mb-4 rounded-lg">
                    {/* This is where the DICOM viewer would be integrated */}
                    <div className="text-white text-center">
                      <p className="text-lg font-medium mb-2">DICOM Image Viewer</p>
                      <p className="text-sm">Click to interact with the imaging study</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-64 bg-base-200 flex justify-center items-center mb-4 rounded-lg">
                    <div className="text-base-content opacity-60 text-center">
                      <svg className="h-12 w-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-lg font-medium mb-1">No Images Available</p>
                      <p className="text-sm">Only report data is available for this study</p>
                    </div>
                  </div>
                )}
                
                {/* Report Details */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-base-content mb-1">Findings</h4>
                    <p className="text-sm text-gray-600">{activeImage.findings}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-base-content mb-1">Impression</h4>
                    <p className="text-sm text-gray-600">{activeImage.impression}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-base-content mb-1">Radiologist</h4>
                    <p className="text-sm text-gray-600">Dr. Michael Chen</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-6 text-center">
              <svg className="h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-medium text-base-content mb-1">No Imaging Selected</h3>
              <p className="text-base-content opacity-60 max-w-sm">Select an imaging study from the list to view details. You can view reports and, where available, interact with the images.</p>
            </div>
          )}
        </div>
      </div>

      {/* Popup DICOM Viewer (would be implemented with proper modal component) */}
      {showImageViewer && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50" onClick={closeImageViewer}>
          <div className="bg-base-100 w-4/5 h-4/5 rounded-lg overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-accent flex justify-between items-center">
              <h3 className="text-lg font-semibold">{activeImage?.title} - Full Viewer</h3>
              <button onClick={closeImageViewer} className="text-base-content opacity-60 hover:text-gray-700">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 bg-black h-full flex items-center justify-center text-white opacity-70">
              {/* Cornerstone/DICOM viewer temporarily disabled — too slow to build (see TE-27). */}
              DICOM viewer temporarily unavailable
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagingTab;
